import { Text } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

type PremiumCardProps = {
  totalValue: string;
  /** Rótulo do valor em destaque. Default "Prêmio Total"; o PME usa
   *  "Prêmio Mensal" porque os valores do card são mensais. */
  totalLabel?: string;
  liquidValue: string;
  interestPercentage: string;
  interestValue: string;
  iofValue: string;
  product: string;
  proposalNumber: string;
  proposalLabel?: string;
  status: string;
  defaultExpanded?: boolean;
  showPisCofins?: boolean;
  isUnderAnalysis?: boolean;
  underAnalysisMessages?: ReactNode[];
  /**
   * Recálculo em andamento (ex.: vida incluída/removida). Só os VALORES ficam
   * indisponíveis — o resto da página segue utilizável, porque o recálculo do
   * PME pode demorar e trocar a tela inteira por um spinner tirava o corretor
   * do lugar onde ele estava trabalhando.
   */
  isRecalculating?: boolean;
};

export function PremiumCard({
  totalValue,
  totalLabel = 'Prêmio Total',
  liquidValue,
  interestPercentage,
  interestValue,
  iofValue,
  product,
  proposalNumber,
  proposalLabel = 'Número da Proposta',
  status,
  defaultExpanded = false,
  showPisCofins = true,
  isUnderAnalysis = false,
  underAnalysisMessages,
  isRecalculating = false,
}: PremiumCardProps) {
  const [collapsePremiumCard, setCollapsePremiumCard] = useState(!defaultExpanded);
  const blurValueClass =
    isUnderAnalysis || isRecalculating ? 'blur-[6px] select-none' : '';

  return (
    <div className='flex w-full'>
      <div className='flex w-full flex-col items-center gap-2'>
        <div className='relative flex flex-col items-center w-full rounded-2xl bg-linear-to-br from-[#2C285F] to-[#18163D] self-stretch px-6 py-6 text-center'>
          <button
            onClick={() => setCollapsePremiumCard(!collapsePremiumCard)}
            className='absolute py-4 px-6 top-0 right-0 cursor-pointer bg-transparent border-none'
          >
            {collapsePremiumCard ? (
              <ChevronDown className='size-6 text-alba-white-500' />
            ) : (
              <ChevronUp className='size-6 text-alba-white-500' />
            )}
          </button>

          <Text className='text-sm text-alba-white-500 mb-2'>{totalLabel}</Text>
          <Text
            className={`text-3xl font-bold leading-[44px] text-alba-white-500 ${blurValueClass}`}
          >
            {totalValue}
          </Text>

          <div
            className={`grid mt-4 ${
              showPisCofins
                ? 'w-full grid-cols-4 gap-x-2'
                : 'mx-auto w-full max-w-[320px] grid-cols-2 gap-x-6'
            }`}
          >
            <div className='flex flex-col items-start gap-y-0'>
              <Text className='text-xs text-alba-white-500'>
                Prêmio Líquido
              </Text>
              <Text
                className={`text-sm font-bold text-alba-white-500 ${blurValueClass}`}
              >
                {liquidValue}
              </Text>
            </div>

            {showPisCofins && (
              <>
                <div className='flex flex-col items-start gap-y-0'>
                  <Text className='text-xs text-alba-white-500'>
                    Percentual de PIS/COFINS
                  </Text>
                  <Text
                    className={`text-sm font-bold text-alba-white-500 ${blurValueClass}`}
                  >
                    {interestPercentage}
                  </Text>
                </div>

                <div className='flex flex-col items-start gap-y-0'>
                  <Text className='text-xs text-alba-white-500'>PIS/COFINS</Text>
                  <Text
                    className={`text-sm font-bold text-alba-white-500 ${blurValueClass}`}
                  >
                    {interestValue}
                  </Text>
                </div>
              </>
            )}

            <div className='flex flex-col items-start gap-y-0'>
              <Text className='text-xs text-alba-white-500'>IOF</Text>
              <Text
                className={`text-sm font-bold text-alba-white-500 ${blurValueClass}`}
              >
                {iofValue}
              </Text>
            </div>
          </div>

          {isRecalculating && (
            <div className='flex items-center justify-center gap-2 mt-4 w-full'>
              <Loader2
                className='h-4 w-4 animate-spin text-alba-white-500'
                strokeWidth={1.5}
              />
              <Text className='text-sm font-semibold text-alba-white-500'>
                Recalculando prêmio…
              </Text>
            </div>
          )}

          {!isRecalculating && isUnderAnalysis && (
            <div className='flex flex-col items-center gap-1 mt-4 w-full'>
              <div className='flex items-center justify-center gap-2'>
                <AlertTriangle
                  className='h-4 w-4 text-[#F46363] relative -top-[1px]'
                  strokeWidth={1.5}
                />
                <Text className='text-sm font-semibold text-[#F46363]'>
                  Cálculo indisponível
                </Text>
              </div>
              {underAnalysisMessages && underAnalysisMessages.length > 0 && (
                <ul className='flex flex-col items-center gap-0.5 max-w-[480px]'>
                  {underAnalysisMessages.map((message, index) => (
                    <li
                      key={index}
                      className='text-xs text-[#F46363] text-center'
                    >
                      {message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!collapsePremiumCard && (
            <div className='mt-4 flex w-full flex-col gap-2 border-t border-[#9FA4A6] pt-4 sm:grid sm:grid-cols-[0.9fr_1.25fr_0.85fr] sm:gap-x-3 sm:gap-y-0'>
              <div className='flex min-w-0 items-center justify-between gap-3 sm:flex-col sm:items-start sm:gap-y-0'>
                <span className='shrink-0 text-sm font-medium text-alba-white-500'>Produto</span>
                <span className='min-w-0 truncate text-right text-sm font-bold text-alba-white-500 sm:text-left sm:whitespace-nowrap'>
                  {product}
                </span>
              </div>

              <div className='flex min-w-0 items-center justify-between gap-3 sm:flex-col sm:items-start sm:gap-y-0'>
                <span className='shrink-0 text-sm font-medium text-alba-white-500'>
                  {proposalLabel}
                </span>
                <span className='min-w-0 truncate text-right text-sm font-bold text-alba-white-500 sm:block sm:w-full sm:overflow-visible sm:text-left sm:whitespace-nowrap'>
                  {proposalNumber}
                </span>
              </div>

              <div className='flex min-w-0 items-center justify-between gap-3 sm:flex-col sm:items-start sm:gap-y-0'>
                <span className='shrink-0 text-sm font-medium text-alba-white-500'>Status</span>
                <span className='min-w-0 truncate text-right text-sm font-bold text-alba-white-500 sm:text-left sm:whitespace-nowrap'>
                  {status}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
